import { Warning } from "@mui/icons-material";
import Check from "@mui/icons-material/Check";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { MouseEvent, useState } from "react";
import { isValidationPossible } from "../../helpers/validators";
import BienIcon from "../../ressources/images/BienIcon.svg";
import BienIconRefus from "../../ressources/images/BienIconRefus.svg";
import { Profil } from "../../services/Api/swagger";
import InputSwitch from "../inputSwitch";
import Statut from "../Statut";
import { formatDateCourt } from "../../helpers/formatters";

interface Props {
  type: "adb" | "utilisateurs" | "dossiersLocation" | "messages";
  donneesTableau: any[];
  alerteEnCoursSuppression: boolean;
  onConsulter(idLigne: string | number): void;
  onDelete(idLigne: string | number): void;
  onGenerer(idLigne: string | number): void; // Generer certificat agrement
  onChangeStatut?(id: string | number, statut: boolean): void;
  isChargeClienteleBesse?: boolean;
  onSetDuplicationDossier?(ref: string): void;
  onSetAccepterDossier?(ref: string): void;
  onSetRefuserDossier?(ref: string): void;
  onSetAccepterRefuserDossier?(dossierLocationItem: any): void;
  isAccepterRefuserLocationLoading?: boolean;
  canAcceptRefuseLocation?: boolean;
  infoProfil?: Profil;
  genererLoading?: boolean;
}

interface State {
  // Etat d'ouverture du menu
  menuOpen: string;
  // Gestion du menu
  anchorEl?: null | HTMLButtonElement;
  accepterOuRefuser: string;
  alerteEnCoursSuppression: boolean;
  messageEnCoursSuppressionDejaAffiche: boolean;
}

const Body: React.FC<Props> = ({
  type,
  donneesTableau,
  alerteEnCoursSuppression,
  onConsulter,
  onDelete,
  onGenerer,
  onChangeStatut,
  isChargeClienteleBesse,
  onSetDuplicationDossier,
  onSetAccepterDossier,
  onSetRefuserDossier,
  onSetAccepterRefuserDossier,
  isAccepterRefuserLocationLoading,
  canAcceptRefuseLocation,
  infoProfil,
  genererLoading,
}) => {
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [accepterOuRefuser, setAccepterOuRefuser] =
    useState<"Accepter" | "Refuser" | "">("");
  const [alerteDelete, setAlerteDelete] = useState(false);
  const [messageAlerteDelete, setMessageAlerteDelete] = useState(false);

  /**
   * Ouverture du menu
   */
  const handleOpen = (e: MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    setMenuOpen(id);
    setAnchorEl(e.currentTarget);
  };

  /**
   * Fermeture du menu
   */
  const handleClose = () => {
    setMenuOpen("");
    setAnchorEl(null);
    setAlerteDelete(false);
  };

  const handleAccepter = (ref: string) => {
    setAccepterOuRefuser("Accepter");
    onSetAccepterDossier && onSetAccepterDossier(ref);
  };

  const handleRefuser = (ref: string) => {
    setAccepterOuRefuser("Refuser");
    onSetRefuserDossier && onSetRefuserDossier(ref);
  };

  const isDeletedSoon = (enCoursSuppression: boolean) => {
    if (alerteEnCoursSuppression && !messageAlerteDelete) {
      setAlerteDelete(true);
      setMessageAlerteDelete(true);
    }
    if (enCoursSuppression) {
      return true;
    }
    return false;
  };

  return (
    <TableBody>
      {donneesTableau.map((n) => {
        let classname = "ligneTableau";
        if (type === "utilisateurs" && !n.actif) {
          classname += " disabled";
        }
        return (
          <TableRow key={n.identifiant} hover={true} className={classname}>
            {type === "adb" && (
              /**
               * Administrateurs de biens
               */
              <>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.identifiant}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.nom}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.adresse2 ? n.adresse1 + " - " + n.adresse2 : n.adresse1}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.codePostal}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.ville}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.commercial
                    ? n.commercial.nom + " " + n.commercial.prenom
                    : ""}
                </TableCell>
                <TableCell className="ignoreDisable">
                  {n.actif && <Check />}
                </TableCell>
              </>
            )}
            {type === "dossiersLocation" && (
              /**
               * Dossier de location
               */
              <React.Fragment>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.site}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.refLocation}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.refMandat}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.numeroLot}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.ville}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.adresse}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.loyerCharges !== 0 && n.loyerCharges + "€"}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  {n.loyerMaxConseille && n.loyerMaxConseille + "€"}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  <div className="td-locreferent">
                    {n.locReferent}
                    {isDeletedSoon(n.enCoursSuppression) && (
                      <div className="deletedSoon">
                        <Tooltip title="Ce dossier de location sera supprimé dans moins de 30 jours">
                          <Warning color="warning" />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell
                  style={{ wordBreak: "inherit" }}
                  onClick={() => onConsulter(n.refLocation)}
                >
                  <div className={n.gli === "Non" ? "gliNon" : "gliOui"}>
                    {n.gli}
                  </div>
                </TableCell>
                <TableCell onClick={() => onConsulter(n.refLocation)}>
                  <Statut statut={n.statut} />
                </TableCell>
                {n.type && (
                  <TableCell onClick={() => onConsulter(n.refLocation)}>
                    {n.type}
                  </TableCell>
                )}
                <TableCell
                  onClick={() => onConsulter(n.refLocation)}
                  className="enPlace"
                >
                  {n.locationAcceptee === 2 && (
                    <img
                      src={BienIconRefus}
                      width="22"
                      height="22"
                      alt="location refusée"
                    />
                  )}
                  {n.locationAcceptee === 1 && (
                    <img
                      src={BienIcon}
                      width="22"
                      height="22"
                      alt="location acceptée"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-controls={"menu-actions" + n.refLocation}
                    aria-haspopup="true"
                    onClick={(e) => handleOpen(e, n.refLocation)}
                    aria-label="Actions sur le dossier"
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    id={"menu-actions" + n.refLocation}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={n.refLocation === menuOpen}
                    onClose={handleClose}
                    className={"menuTableau"}
                    classes={{ paper: "menuTableau" }}
                  >
                    <MenuItem
                      component={Link}
                      onClick={() => onConsulter(n.refLocation)}
                    >
                      Consulter / Modifier
                    </MenuItem>
                    {infoProfil &&
                      infoProfil.fonctions &&
                      infoProfil.fonctions.find(
                        (f) => f === "TDBDOSLOC_SUPPRESSION",
                      ) &&
                      (n.statut === "INCOMPLET" ||
                        n.statut === "NON_VALIDE") && (
                        <MenuItem
                          onClick={() => {
                            onDelete(n.refLocation);
                          }}
                        >
                          {" "}
                          Supprimer le dossier
                        </MenuItem>
                      )}
                    {n.locationAcceptee === 1 && n.statut === "VALIDE_AGREE" && (
                      <MenuItem
                        onClick={() => {
                          onGenerer(n.refLocation);
                        }}
                      >
                        {" "}
                        Génerer certificat d'agrement
                        {genererLoading && (
                          <CircularProgress
                            className={"menuSpinner"}
                            size={17}
                          />
                        )}
                      </MenuItem>
                    )}
                    {(n.statut === "VALIDE" ||
                      n.statut === "VALIDE_AGREE" ||
                      n.statut === "VALIDE_AGREE_DIFFERE" ||
                      n.statut === "VALIDE_NON_AGREE") &&
                      (!isValidationPossible(n.dateDemandeValidation) ? (
                        <Tooltip title="Ce dossier a dépassé la delai pour la duplication">
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.7,
                              padding: "4px 16px",
                              margin: 0,
                            }}
                            className={"button disabledButton"}
                          >
                            Dupliquer
                          </Typography>
                        </Tooltip>
                      ) : (
                        <MenuItem
                          component={Link}
                          onClick={() =>
                            onSetDuplicationDossier &&
                            onSetDuplicationDossier(n.refLocation)
                          }
                        >
                          Dupliquer
                        </MenuItem>
                      ))}
                    {(n.statut === "VALIDE" ||
                      n.statut === "VALIDE_AGREE" ||
                      n.statut === "VALIDE_AGREE_DIFFERE" ||
                      n.statut === "VALIDE_NON_AGREE") &&
                      canAcceptRefuseLocation &&
                      n.locationAcceptee !== 0 &&
                      n.locationAcceptee !== null && (
                        <MenuItem
                          component={Link}
                          onClick={() =>
                            onSetAccepterRefuserDossier &&
                            onSetAccepterRefuserDossier(n)
                          }
                        >
                          {n.locationAcceptee === 1
                            ? "Refuser location"
                            : "Accepter location"}

                          {isAccepterRefuserLocationLoading && (
                            <CircularProgress
                              className={"menuSpinner"}
                              size={17}
                            />
                          )}
                        </MenuItem>
                      )}
                    {(n.statut === "VALIDE_AGREE" ||
                      n.statut === "VALIDE_AGREE_DIFFERE") &&
                      canAcceptRefuseLocation &&
                      (n.locationAcceptee === 0 ||
                        n.locationAcceptee === null) && (
                        <MenuItem
                          component={Link}
                          onClick={() => handleAccepter(n.refLocation)}
                        >
                          Accepter location
                          {isAccepterRefuserLocationLoading &&
                            accepterOuRefuser === "Accepter" && (
                              <CircularProgress
                                className={"menuSpinner"}
                                size={17}
                              />
                            )}
                        </MenuItem>
                      )}
                    {(n.statut === "VALIDE" ||
                      n.statut === "VALIDE_AGREE" ||
                      n.statut === "VALIDE_AGREE_DIFFERE" ||
                      n.statut === "VALIDE_NON_AGREE") &&
                      canAcceptRefuseLocation &&
                      (n.locationAcceptee === 0 ||
                        n.locationAcceptee === null) && (
                        <MenuItem
                          component={Link}
                          onClick={() => handleRefuser(n.refLocation)}
                        >
                          Refuser location
                          {isAccepterRefuserLocationLoading &&
                            accepterOuRefuser === "Refuser" && (
                              <CircularProgress
                                className={"menuSpinner"}
                                size={17}
                              />
                            )}
                        </MenuItem>
                      )}
                  </Menu>
                </TableCell>
              </React.Fragment>
            )}
            {type === "utilisateurs" && (
              /**
               * Utilisateurs
               */
              <React.Fragment>
                {n.numAdherent && (
                  <TableCell onClick={() => onConsulter(n.identifiant)}>
                    {n.numAdherent}
                  </TableCell>
                )}
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.nom}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.prenom}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.mail}
                </TableCell>
                {n.fonction && (
                  <TableCell onClick={() => onConsulter(n.identifiant)}>
                    {n.fonction.libelle}
                  </TableCell>
                )}
                <TableCell onClick={() => onConsulter(n.identifiant)}>
                  {n.profil && n.profil}
                </TableCell>
                <TableCell className="ignoreDisable">
                  <InputSwitch
                    disabled={isChargeClienteleBesse}
                    id={n.identifiant}
                    onChange={onChangeStatut}
                    value={n.actif}
                    label="Actif"
                  />
                </TableCell>
              </React.Fragment>
            )}
            {type === "messages" && (
              <React.Fragment>
                <TableCell onClick={() => onConsulter(n.id)}>
                  {n.titre}
                </TableCell>
                <TableCell
                  onClick={() => onConsulter(n.id)}
                  sx={{ maxWidth: "400px" }}
                >
                  {n.contenu}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.id)}>
                  {formatDateCourt(n.dateDebut)}
                </TableCell>
                <TableCell onClick={() => onConsulter(n.id)}>
                  {formatDateCourt(n.dateFin)}
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-controls={"menu-actions-messages" + n.id}
                    aria-haspopup="true"
                    onClick={(e) => handleOpen(e, n.id)}
                    aria-label="Actions sur le message"
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    id={"menu-actions-messages" + n.id}
                    open={n.id === menuOpen}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    onClose={handleClose}
                  >
                    <MenuItem
                      component={Link}
                      onClick={() => onConsulter(n.id)}
                    >
                      Modifier
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        onDelete(n.id);
                      }}
                    >
                      Supprimer
                    </MenuItem>
                  </Menu>
                </TableCell>
              </React.Fragment>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default Body;
